import React from 'react';
import type { Observation, FinalSummary } from '../../types';

/**
 * Stage 3 — "Data Quality Observations" callout.
 *
 * Bordered card with subtle cyan edge. Distinct visual weight from
 * regular text — signals "these are findings the user should review".
 *
 * Each observation: bold category label + colon + description.
 */

interface FindingsCalloutProps {
  observations: Observation[];
  className?: string;
  title?: string;
}

export const FindingsCallout: React.FC<FindingsCalloutProps> = ({
  observations, title = 'Data Quality Observations', className = '',
}) => (
  <div
    className={`
      rounded-lg border border-cyan-500/30
      bg-cyan-950/20
      px-5 py-4
      shadow-[inset_0_1px_0_rgba(8,145,178,0.1)]
      ${className}
    `}
  >
    <div className="text-cargo-text font-semibold text-[15px] mb-3">
      {title}
    </div>
    <ul className="space-y-2">
      {observations.map((o, i) => (
        <li key={i} className="flex items-start gap-2 text-cargo-muted text-[13px] leading-relaxed">
          <span className="mt-2 w-1 h-1 rounded-full bg-cargo-muted flex-shrink-0" />
          <span>
            <span className="text-cargo-text font-semibold">{o.category}</span>
            <span className="text-cargo-muted">: </span>
            <span>{o.description}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Stage 8 — "Summary of Required Actions Before Load" callout.
 *
 * Bordered card with 3 sub-buckets:
 *   - Must Fix (Blocks Load)        — items 1-3 (CRITICAL bucket items)
 *   - Should Fix (Data Quality)     — items 4-N (HIGH + MEDIUM)
 *   - Optional Improvements         — items N+1 onward (LOW)
 *
 * Must Fix and Should Fix entries get ✅ checkmarks; optional don't.
 */

interface FinalSummaryCalloutProps {
  summary: FinalSummary;
  className?: string;
}

export const FinalSummaryCallout: React.FC<FinalSummaryCalloutProps> = ({
  summary, className = '',
}) => (
  <div
    className={`
      rounded-xl border border-cyan-500/40
      bg-cyan-950/15
      p-6
      shadow-[inset_0_1px_0_rgba(8,145,178,0.1)]
      ${className}
    `}
  >
    <h3 className="text-cargo-text font-semibold text-[16px] mb-5">
      Summary of Required Actions Before Load
    </h3>

    {summary.must_fix.length > 0 && (
      <SubBucket
        title="Must Fix (Blocks Load):"
        entries={summary.must_fix}
        showCheck={true}
      />
    )}

    {summary.should_fix.length > 0 && (
      <SubBucket
        title="Should Fix (Data Quality):"
        entries={summary.should_fix}
        showCheck={true}
        className="mt-5"
      />
    )}

    {summary.optional.length > 0 && (
      <SubBucket
        title="Optional Improvements:"
        entries={summary.optional}
        showCheck={false}
        className="mt-5"
      />
    )}
  </div>
);

interface SubBucketProps {
  title: string;
  entries: { number: number; column_name: string; action: string }[];
  showCheck: boolean;
  className?: string;
}

const SubBucket: React.FC<SubBucketProps> = ({ title, entries, showCheck, className = '' }) => (
  <div className={className}>
    <div className="text-cargo-text font-semibold text-[13px] mb-2.5">{title}</div>
    <ol className="space-y-1.5 ml-1">
      {entries.map((e) => (
        <li key={e.number} className="flex items-start gap-2 text-[13px] leading-relaxed">
          <span className="text-cargo-muted w-6 flex-shrink-0">{e.number}.</span>
          {showCheck && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-cargo-success text-cargo-success text-[10px] font-bold flex-shrink-0 mt-0.5">
              ✓
            </span>
          )}
          <span>
            <span className="text-cargo-text font-semibold">{e.column_name}</span>
            <span className="text-cargo-muted">: </span>
            <span className="text-cargo-muted">{e.action}</span>
          </span>
        </li>
      ))}
    </ol>
  </div>
);
